import { toCanvas, toPng } from 'html-to-image';
import { ElementRef } from '@angular/core';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { LoggerService } from '@optima-stride-ai/core';



/**
 * Utility class for generating thumbnails from DOM elements using html2canvas
 */
export class Html2CanvasThumbnailHelper {
  static logger: LoggerService;
  
  /**
   * Generate thumbnail from DOM element using html2canvas
   * @param element - ElementRef or HTMLElement to capture
   * @param options - Configuration options for thumbnail generation
   * @returns Promise<string> - Base64 data URL of the generated thumbnail
   */
  static async generateThumbnailFromElement(
    element: ElementRef | HTMLElement,
    options: Html2CanvasThumbnailOptions = {}
  ): Promise<string> {
    try {
      const targetElement = element instanceof ElementRef ? element.nativeElement : element;
      
      if (!targetElement) {
        throw new Error('Invalid element provided for thumbnail generation');
      }

      const canvas = await toCanvas(targetElement, {
        backgroundColor: options.backgroundColor || '#ffffff',
        width: options.width || 800,
        height: options.height || 1000,
        style: {
          transform: `scale(${options.scale || 0.3})`,
        },
        pixelRatio: options.scale || 0.3
      });

      // Crop to first page if cropHeight is specified
      if (options.cropHeight) {
        return this.cropCanvas(canvas, options.cropHeight, options.imageFormat, options.quality);
      }

      // Return full canvas as data URL
      return canvas.toDataURL(options.imageFormat || 'image/jpg', options.quality || 0.8);
      
    } catch (error) {
      this.logger?.error(`Error generating thumbnail from element: ${error}`);
      throw error;
    }
  }

  /**
   * Generate thumbnail specifically for markdown preview div
   * Optimized settings for markdown content
   */
  static async generateMarkdownPreviewThumbnail(
    previewElement: ElementRef | HTMLElement, name?: string
  ): Promise<string> {
    return this.generateThumbnailFromElement(previewElement, {
      scale: 0.3,
      height: 1000,
      width: 800,
      cropHeight: 600,
      imageFormat: 'image/jpg',
      quality: 0.8,
      backgroundColor: '#ffffff'
    });
  }

  /**
   * Generate markdown thumbnail from content text
   * Creates a clean preview showing only the markdown content
   */
  static createMarkdownThumbnailFromContent(
    content: string,
    fileName: string = 'Untitled'
  ): string {
    const canvas = document.createElement('canvas');
    canvas.width = 380;
    canvas.height = 320;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      this.logger?.error('❌ Cannot create canvas context for markdown thumbnail');
      return '';
    }

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw only the markdown content
    this.drawMarkdownContentOnly(ctx, content);
    
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  /**
   * Draw only markdown content without any decorations
   */
  private static drawMarkdownContentOnly(ctx: CanvasRenderingContext2D, content: string): void {
    // Content area with padding
    const contentX = 20;
    const contentY = 20;
    const contentWidth = 340;
    
    // Process and render markdown content
    const lines = content.split('\n');
    let yPos = contentY;
    const lineHeight = 16;
    const maxLines = 18;
    let lineCount = 0;
    
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    for (let i = 0; i < lines.length && lineCount < maxLines; i++) {
      const line = lines[i];
      
      // Skip if we're out of space
      if (yPos + lineHeight > 300) {
        break;
      }
      
      let displayLine = line.trim();
      let fontSize = 12;
      let fontWeight = '400';
      let color = '#202124';
      
      // Parse markdown syntax
      if (displayLine.startsWith('# ')) {
        // H1
        displayLine = displayLine.substring(2);
        fontSize = 18;
        fontWeight = '600';
        color = '#1a1a1a';
      } else if (displayLine.startsWith('## ')) {
        // H2
        displayLine = displayLine.substring(3);
        fontSize = 16;
        fontWeight = '600';
        color = '#2d2d2d';
      } else if (displayLine.startsWith('### ')) {
        // H3
        displayLine = displayLine.substring(4);
        fontSize = 14;
        fontWeight = '600';
        color = '#404040';
      } else if (displayLine.startsWith('- ') || displayLine.startsWith('* ')) {
        // List item
        displayLine = '• ' + displayLine.substring(2);
        color = '#3c4043';
      } else if (displayLine.startsWith('> ')) {
        // Blockquote
        ctx.fillStyle = '#e8eaed';
        ctx.fillRect(contentX, yPos, 4, lineHeight);
        displayLine = displayLine.substring(2);
        color = '#5f6368';
        fontWeight = '400';
      } else if (displayLine.match(/^\d+\. /)) {
        // Numbered list
        color = '#3c4043';
      }
      
      // Set font
      ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = color;
      
      // Wrap text if needed
      if (displayLine.length > 0) {
        const maxWidth = contentWidth - (displayLine.startsWith('> ') ? 10 : 0);
        const words = displayLine.split(' ');
        let currentLine = '';
        
        for (const word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > maxWidth && currentLine) {
            // Draw current line
            const xOffset = displayLine.startsWith('> ') ? 10 : 0;
            ctx.fillText(currentLine, contentX + xOffset, yPos);
            yPos += lineHeight;
            lineCount++;
            currentLine = word;
            
            if (lineCount >= maxLines) break;
          } else {
            currentLine = testLine;
          }
        }
        
        // Draw remaining text
        if (currentLine && lineCount < maxLines) {
          const xOffset = displayLine.startsWith('> ') ? 10 : 0;
          ctx.fillText(currentLine, contentX + xOffset, yPos);
          yPos += lineHeight;
          lineCount++;
        }
      } else {
        // Empty line - add smaller spacing
        yPos += lineHeight * 0.5;
      }
      
      // Add extra spacing after headers
      if (displayLine.startsWith('#')) {
        yPos += lineHeight * 0.3;
      }
    }
  }

  /**
   * Generate thumbnail for Collabora document iframe
   * Attempts to capture visible content, falls back to document info thumbnail
   */
  static async generateCollaboraThumbnail(
    iframeElement: ElementRef | HTMLElement,
    documentInfo?: { name?: string; size?: number; type?: string }
  ): Promise<string> {
    try {
      const targetElement = iframeElement instanceof ElementRef ? iframeElement.nativeElement : iframeElement;
      
      if (!targetElement) {
        throw new Error('Invalid iframe element provided');
      }

      // Try to capture the iframe content
      try {
        const canvas = await toCanvas(targetElement, {
          backgroundColor: '#ffffff',
          width: 1200,
          height: 800,
          style: {
            transform: 'scale(0.25)',
          },
          pixelRatio: 0.25
        });

        // Check if we got actual content or just blank iframe
        const ctx = canvas.getContext('2d');
        const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
        const isBlank = this.isCanvasBlank(imageData);

        if (!isBlank) {
          this.logger?.info('✅ Successfully captured Collabora iframe content');
          return canvas.toDataURL('image/jpeg', 0.8);
        } else {
          this.logger?.warn('⚠️ Iframe appears blank, creating document info thumbnail');
          throw new Error('Blank iframe detected');
        }

      } catch (captureError) {
        this.logger?.warn('⚠️ Cannot capture iframe content (CORS/security), creating document info thumbnail');
        // Create a document info thumbnail instead
        return this.createDocumentInfoThumbnail(documentInfo);
      }

    } catch (error) {
      this.logger?.error(`Error generating Collabora thumbnail: ${error}`);
      // Fallback to document info thumbnail
      return this.createDocumentInfoThumbnail(documentInfo);
    }
  }

  /**
   * Create a document information thumbnail when iframe capture fails
   */
  static createDocumentInfoThumbnail(
    documentInfo?: { name?: string; size?: number; type?: string }
  ): string {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 300;
    const ctx = canvas.getContext('2d')!;

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Border
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 2;
    ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);

    // Document icon area
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(20, 25, canvas.width - 40, 40);

    // Document type indicator
    const docType = this.getDocumentType(documentInfo?.name || '');
    ctx.fillStyle = docType.color;
    ctx.fillRect(canvas.width - 50, 20, 35, 20);
    
    // Document type text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(docType.label, canvas.width - 32.5, 33);

    // Document name
    ctx.fillStyle = '#333';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    const name = documentInfo?.name || 'Document';
    const truncatedName = name.length > 20 ? name.substring(0, 20) + '...' : name;
    ctx.fillText(truncatedName, 25, 45);

    // Content lines (simulating document content)
    ctx.fillStyle = '#666';
    const lineHeight = 8;
    const startY = 80;
    for (let i = 0; i < 12; i++) {
      const lineWidth = Math.random() * 100 + 80; // Random line widths
      ctx.fillRect(25, startY + i * lineHeight, lineWidth, 3);
    }

    // Table representation for spreadsheets
    if (docType.label === 'XLS') {
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      
      // Draw grid
      const gridX = 25, gridY = 180, gridW = 180, gridH = 80;
      ctx.strokeRect(gridX, gridY, gridW, gridH);
      
      // Grid lines
      for (let i = 1; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(gridX + (gridW / 4) * i, gridY);
        ctx.lineTo(gridX + (gridW / 4) * i, gridY + gridH);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(gridX, gridY + (gridH / 4) * i);
        ctx.lineTo(gridX + gridW, gridY + (gridH / 4) * i);
        ctx.stroke();
      }
      
      // Sample data points
      ctx.fillStyle = '#999';
      ctx.font = '8px Arial';
      ctx.fillText('A1', gridX + 10, gridY + 15);
      ctx.fillText('B1', gridX + 55, gridY + 15);
      ctx.fillText('Data', gridX + 10, gridY + 35);
      ctx.fillText('100', gridX + 55, gridY + 35);
    }

    // File info
    ctx.fillStyle = '#999';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    const sizeText = documentInfo?.size ? `${Math.round(documentInfo.size / 1024)}KB` : 'Document';
    ctx.fillText(sizeText, canvas.width / 2, canvas.height - 20);

    this.logger?.debug('✅ Created document info thumbnail');
    return canvas.toDataURL('image/jpeg', 0.8);
  }

  /**
   * Check if canvas is blank (all pixels are the same color)
   */
  static isCanvasBlank(imageData: ImageData | undefined): boolean {
    if (!imageData) return true;
    
    const { data } = imageData;
    const firstPixel = [data[0], data[1], data[2], data[3]];
    
    // Check if all pixels are the same as the first pixel
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] !== firstPixel[0] || 
          data[i + 1] !== firstPixel[1] || 
          data[i + 2] !== firstPixel[2] || 
          data[i + 3] !== firstPixel[3]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Get document type info for thumbnail
   */
  static getDocumentType(filename: string): { label: string; color: string } {
    const ext = filename.toLowerCase().split('.').pop();
    
    switch (ext) {
      case 'docx':
      case 'doc':
      case 'odt':
        return { label: 'DOC', color: '#2B579A' };
      case 'xlsx':
      case 'xls':
      case 'ods':
        return { label: 'XLS', color: '#217346' };
      case 'pptx':
      case 'ppt':
      case 'odp':
        return { label: 'PPT', color: '#D24726' };
      case 'pdf':
        return { label: 'PDF', color: '#FF0000' };
      default:
        return { label: 'DOC', color: '#666666' };
    }
  }

  /**
   * Generate and download thumbnail from DOM element
   */
  static async generateAndDownloadThumbnail(
    element: ElementRef | HTMLElement,
    filename?: string,
    options: Html2CanvasThumbnailOptions = {}
  ): Promise<void> {
    try {
      const dataUrl = await this.generateThumbnailFromElement(element, options);
      this.downloadThumbnail(dataUrl, filename);
    } catch (error) {
      this.logger?.error(`Error generating and downloading thumbnail: ${error}`);
      throw error;
    }
  }
static async generateZipFile(file1: File, file2: File): Promise<Blob> {
  const zip = new JSZip();

  zip.file(file1.name, file1);
  zip.file(file2.name, file2);
  const content = await zip.generateAsync({ type: "blob" });
//    saveAs(content, "files.zip");
  return content;
}

  /**
   * Crop canvas to specified height and convert to data URL
   */
  private static cropCanvas(
    sourceCanvas: HTMLCanvasElement, 
    cropHeight: number,
    imageFormat: string = 'image/png',
    quality: number = 0.8
  ): string {
    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = sourceCanvas.width;
    croppedCanvas.height = Math.min(cropHeight, sourceCanvas.height);

    const croppedCtx = croppedCanvas.getContext('2d');
    if (!croppedCtx) {
      throw new Error('Failed to get canvas context for cropping');
    }

    croppedCtx.drawImage(sourceCanvas, 0, 0);
    return croppedCanvas.toDataURL(imageFormat, quality);
  }

  /**
   * Download thumbnail as file
   */
  private static downloadThumbnail(dataUrl: string, filename?: string): void {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename || `thumbnail-${Date.now()}.png`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    this.logger?.debug(`Thumbnail downloaded as: ${link.download}`);
  }

  /**
   * Convert data URL to Blob
   */
  static dataUrlToBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new Blob([u8arr], { type: mime });
  }

  /**
   * Get canvas element from data URL for further processing
   */
  static async dataUrlToCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        resolve(canvas);
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }
}

/**
 * Configuration options for Html2Canvas thumbnail generation
 */
export interface Html2CanvasThumbnailOptions {
  /** Scale factor for the output image (0.1 to 2) */
  scale?: number;
  /** Target width for html2canvas */
  width?: number;
  /** Target height for html2canvas */
  height?: number;
  /** Height to crop the final image to (useful for showing just first page) */
  cropHeight?: number;
  /** Background color for the canvas */
  backgroundColor?: string;
  /** Image format for output */
  imageFormat?: 'image/png' | 'image/jpg' | 'image/webp';
  /** Quality for lossy formats (0-1) */
  quality?: number;
  /** Allow tainted canvases */
  allowTaint?: boolean;
  /** Use CORS for cross-origin images */
  useCORS?: boolean;
  /** Enable logging */
  logging?: boolean;
  /** Additional html2canvas options */
  html2canvasOptions?: any;
}

/**
 * Preset configurations for common use cases
 */
export const Html2CanvasPresets = {
  /** High quality preview */
  highQuality: {
    scale: 0.5,
    width: 1200,
    height: 1600,
    imageFormat: 'image/png' as const,
    quality: 1.0
  },
  /** Standard markdown preview */
  markdownPreview: {
    scale: 0.3,
    width: 800,
    height: 1000,
    cropHeight: 600,
    imageFormat: 'image/png' as const,
    quality: 0.8
  },
  /** Small thumbnail */
  smallThumbnail: {
    scale: 0.2,
    width: 400,
    height: 600,
    cropHeight: 300,
    imageFormat: 'image/jpeg' as const,
    quality: 0.7
  },
  /** Fast preview (lower quality) */
  fastPreview: {
    scale: 0.1,
    width: 300,
    height: 400,
    imageFormat: 'image/jpeg' as const,
    quality: 0.5
  }
} as const;


